/**
 * The IPA symbols Azure reports for English, each with a word a non-linguist can recognise it by.
 *
 * Exists so a target sound is picked from a list rather than typed: several symbols have ASCII
 * look-alikes that are different characters entirely — /ɡ/ is U+0261 and /ɹ/ is U+0279 — and a
 * typo there matches nothing Azure emits, which reads as a scoring failure rather than a mistake.
 */
export interface IpaPhoneme {
  symbol: string;
  /** How the sound is said aloud, so an editor never has to read the symbol. */
  friendly: string;
  /** Word the sound is heard in, which separates the ones whose friendly names collide. */
  example: string;
}

export const IPA_PHONEMES: IpaPhoneme[] = [
  { symbol: "b", friendly: "buh", example: "bottle" },
  { symbol: "p", friendly: "puh", example: "pig" },
  { symbol: "t", friendly: "tuh", example: "top" },
  { symbol: "d", friendly: "duh", example: "dog" },
  { symbol: "k", friendly: "kuh", example: "cat" },
  { symbol: "ɡ", friendly: "guh", example: "goat" },
  { symbol: "f", friendly: "fff", example: "fish" },
  { symbol: "v", friendly: "vvv", example: "van" },
  { symbol: "s", friendly: "sss", example: "sun" },
  { symbol: "z", friendly: "zzz", example: "zip" },
  { symbol: "ʃ", friendly: "shh", example: "ship" },
  { symbol: "ʒ", friendly: "zhh", example: "treasure" },
  { symbol: "tʃ", friendly: "chuh", example: "chair" },
  { symbol: "dʒ", friendly: "juh", example: "jam" },
  { symbol: "θ", friendly: "thh", example: "thumb" },
  { symbol: "ð", friendly: "thuh", example: "this" },
  { symbol: "m", friendly: "mmm", example: "moon" },
  { symbol: "n", friendly: "nnn", example: "nest" },
  { symbol: "ŋ", friendly: "ing", example: "ring" },
  { symbol: "l", friendly: "lll", example: "leaf" },
  { symbol: "ɹ", friendly: "rrr", example: "rain" },
  { symbol: "w", friendly: "wuh", example: "web" },
  { symbol: "j", friendly: "yuh", example: "yes" },
  { symbol: "h", friendly: "huh", example: "hat" },

  { symbol: "æ", friendly: "short a", example: "cat" },
  { symbol: "ɛ", friendly: "short e", example: "bed" },
  { symbol: "ɪ", friendly: "short i", example: "pig" },
  { symbol: "ɑ", friendly: "ah", example: "hot" },
  { symbol: "ɔ", friendly: "aw", example: "saw" },
  { symbol: "ʌ", friendly: "short u", example: "cup" },
  { symbol: "ə", friendly: "schwa, a lazy uh", example: "about" },
  { symbol: "ʊ", friendly: "short oo", example: "book" },
  { symbol: "u", friendly: "long oo", example: "moon" },
  { symbol: "i", friendly: "long e, ee", example: "tree" },
  { symbol: "ɝ", friendly: "er", example: "her" },
  { symbol: "eɪ", friendly: "long a, ay", example: "cake" },
  { symbol: "aɪ", friendly: "long i, eye", example: "bike" },
  { symbol: "oʊ", friendly: "long o, oh", example: "boat" },
  { symbol: "aʊ", friendly: "ow", example: "cloud" },
  { symbol: "ɔɪ", friendly: "oy", example: "coin" },
];

/** True when a symbol is one Azure is known to emit, so a stored target can still be matched. */
export function isKnownIpaPhoneme(symbol: string): boolean {
  return IPA_PHONEMES.some((phoneme) => phoneme.symbol === symbol.normalize("NFC"));
}
